// The theme engine: from the host's few tokens to every CSS variable of a designed light and dark palette.
// The rest of the app only reads these variables. Pure, tested against hostile host colors in theme.test.ts.
import type { Theme, WhiteLabel } from "@imagora/contracts";
import { contrast, fitGamut, parseColor, toCss, type Oklch } from "./color";

export type ThemeMode = "light" | "dark" | "system";
export type ResolvedMode = "light" | "dark";
export type ThemeVars = Record<string, string>;

export const DEFAULT_PRIMARY: Oklch = { l: 0.47, c: 0.095, h: 172 };
export const DEFAULT_PRODUCT_NAME = "Imagora";

const AA_TEXT = 4.5;
const AA_UI = 3;
const BLACK: Oklch = { l: 0, c: 0, h: 0 };
const WHITE: Oklch = { l: 1, c: 0, h: 0 };

/** Moves lightness in steps until the color reaches the contrast against every background, or the end of the range. */
function shiftUntil(color: Oklch, backgrounds: Oklch[], ratio: number, direction: 1 | -1): Oklch {
  let current = fitGamut(color);
  for (let i = 0; i < 100; i += 1) {
    if (backgrounds.every((bg) => contrast(current, bg) >= ratio)) return current;
    const l = current.l + direction * 0.01;
    if (l < 0 || l > 1) return direction === 1 ? WHITE : BLACK;
    current = fitGamut({ ...current, l });
  }
  return current;
}

export interface PrimaryPair {
  primary: Oklch;
  onPrimary: Oklch;
  /** The host's pair failed AA and was replaced. */
  adjusted: boolean;
}

const MAX_PRIMARY_SHIFT = 0.15;

/**
 * A readable button color. The host's own pair if it passes AA. Otherwise the host's text color with the primary nudged
 * just enough, which keeps its look. If that needs too large a move, the host's primary with black or white text.
 */
export function readablePair(primary: Oklch, onPrimary: Oklch | null): PrimaryPair {
  const best = contrast(WHITE, primary) >= Math.min(AA_TEXT, contrast(BLACK, primary)) ? WHITE : BLACK;
  if (!onPrimary) return { primary, onPrimary: best, adjusted: false };
  if (contrast(onPrimary, primary) >= AA_TEXT) return { primary, onPrimary, adjusted: false };
  const direction = contrast(BLACK, onPrimary) > contrast(WHITE, onPrimary) ? -1 : 1;
  const nudged = shiftUntil(primary, [onPrimary], AA_TEXT, direction);
  if (Math.abs(nudged.l - primary.l) <= MAX_PRIMARY_SHIFT && contrast(onPrimary, nudged) >= AA_TEXT) {
    return { primary: nudged, onPrimary, adjusted: true };
  }
  return { primary, onPrimary: best, adjusted: true };
}

function neutral(hue: number, sourceChroma: number) {
  // Neutrals lean to the brand hue just enough to feel related. A gray brand gets true grays.
  const scale = Math.min(1, sourceChroma / 0.08);
  return (l: number, c: number): Oklch => ({ l, c: c * scale, h: hue });
}

function statusVars(mode: ResolvedMode, surfaces: Oklch[]): ThemeVars {
  const hues = { success: 155, warning: 75, danger: 25, info: 245 };
  const vars: ThemeVars = {};
  for (const [name, h] of Object.entries(hues)) {
    const soft: Oklch = mode === "light" ? { l: 0.955, c: 0.035, h } : { l: 0.29, c: 0.05, h };
    const start: Oklch = mode === "light" ? { l: 0.5, c: 0.13, h } : { l: 0.82, c: 0.1, h };
    const text = shiftUntil(start, [soft, ...surfaces], AA_TEXT, mode === "light" ? -1 : 1);
    vars[name] = toCss(text);
    vars[`${name}-soft`] = toCss(soft);
  }
  const danger: Oklch = mode === "light" ? { l: 0.53, c: 0.19, h: 27 } : { l: 0.62, c: 0.19, h: 25 };
  const dangerSolid = mode === "light" ? shiftUntil(danger, [WHITE], AA_TEXT, -1) : shiftUntil(danger, [BLACK], AA_TEXT, 1);
  vars["danger-solid"] = toCss(dangerSolid);
  vars["danger-solid-hover"] = toCss({ ...dangerSolid, l: dangerSolid.l + (mode === "light" ? -0.05 : 0.04) });
  vars["on-danger"] = toCss(mode === "light" ? WHITE : BLACK);
  return vars;
}

function lightVars(hostPrimary: Oklch, hostOn: Oklch | null): ThemeVars {
  const n = neutral(hostPrimary.h, hostPrimary.c);
  const surface = WHITE;
  const bg = n(0.982, 0.005);
  const sunken = n(0.962, 0.008);

  // Nearly white brand colors would make buttons vanish on the page.
  const visible = shiftUntil(hostPrimary, [surface], 1.6, -1);
  const { primary, onPrimary } = readablePair(visible, hostOn);
  const soft: Oklch = { l: 0.955, c: Math.min(0.035, primary.c * 0.35), h: primary.h };
  const softHover: Oklch = { ...soft, l: 0.925, c: Math.min(0.05, primary.c * 0.45) };
  const text = shiftUntil({ ...primary, l: Math.min(primary.l, 0.55) }, [surface, bg, soft, softHover], AA_TEXT, -1);
  const ring = shiftUntil(primary, [surface, bg], AA_UI, -1);

  return {
    bg: toCss(bg),
    surface: toCss(surface),
    "surface-raised": toCss(surface),
    "surface-sunken": toCss(sunken),
    "surface-hover": toCss(n(0.955, 0.009)),
    "surface-active": toCss(n(0.925, 0.011)),
    border: toCss(n(0.915, 0.01)),
    "border-strong": toCss(n(0.84, 0.012)),
    "border-input": toCss(shiftUntil(n(0.66, 0.015), [surface, bg], AA_UI, -1)),
    fg: toCss(n(0.23, 0.018)),
    "fg-muted": toCss(shiftUntil(n(0.46, 0.018), [surface, bg, sunken, n(0.955, 0.009)], AA_TEXT, -1)),
    "fg-subtle": toCss(shiftUntil(n(0.53, 0.016), [surface, bg], AA_TEXT, -1)),
    // The second series of a chart: told apart from the primary's dark mark and still 3:1 on the card.
    "chart-context": toCss(shiftUntil(n(0.74, 0.012), [surface, bg], AA_UI, -1)),
    primary: toCss(primary),
    "primary-hover": toCss({ ...primary, l: primary.l - 0.04 }),
    "primary-active": toCss({ ...primary, l: primary.l - 0.08 }),
    "on-primary": toCss(onPrimary),
    "primary-soft": toCss(soft),
    "primary-soft-hover": toCss(softHover),
    "primary-text": toCss(text),
    ring: toCss(ring),
    "ring-soft": toCss(ring, 0.28),
    scrim: toCss(n(0.2, 0.02), 0.42),
    "shadow-color": toCss(n(0.25, 0.03), 0.1),
    ...statusVars("light", [surface, bg]),
  };
}

function darkVars(hostPrimary: Oklch, hostOn: Oklch | null): ThemeVars {
  const n = neutral(hostPrimary.h, hostPrimary.c);
  const bg = n(0.168, 0.012);
  const surface = n(0.208, 0.014);
  const raised = n(0.245, 0.016);
  const hover = n(0.265, 0.016);

  // A dark brand color disappears on a dark page, so dark mode gets a lighter, slightly quieter version of it.
  const quieter: Oklch = { ...hostPrimary, c: Math.min(hostPrimary.c, 0.17) };
  const visible = shiftUntil(quieter, [surface, raised], AA_UI, 1);
  const { primary, onPrimary } = readablePair(visible, hostOn);
  const soft: Oklch = { l: 0.3, c: Math.min(0.06, primary.c * 0.5), h: primary.h };
  const softHover: Oklch = { ...soft, l: 0.34 };
  const text = shiftUntil({ ...primary, l: Math.max(primary.l, 0.75), c: Math.min(primary.c, 0.13) }, [surface, bg, raised, soft, softHover], AA_TEXT, 1);
  const ring = shiftUntil(primary, [surface, bg, raised], AA_UI, 1);

  return {
    bg: toCss(bg),
    surface: toCss(surface),
    "surface-raised": toCss(raised),
    "surface-sunken": toCss(n(0.148, 0.011)),
    "surface-hover": toCss(hover),
    "surface-active": toCss(n(0.295, 0.018)),
    border: toCss(n(0.285, 0.016)),
    "border-strong": toCss(n(0.37, 0.018)),
    "border-input": toCss(shiftUntil(n(0.5, 0.018), [surface, bg, raised], AA_UI, 1)),
    fg: toCss(n(0.95, 0.006)),
    "fg-muted": toCss(shiftUntil(n(0.77, 0.012), [surface, bg, raised, hover], AA_TEXT, 1)),
    "fg-subtle": toCss(shiftUntil(n(0.7, 0.012), [surface, bg], AA_TEXT, 1)),
    "chart-context": toCss(shiftUntil(n(0.74, 0.012), [surface, bg], AA_UI, 1)),
    primary: toCss(primary),
    "primary-hover": toCss({ ...primary, l: primary.l + 0.035 }),
    "primary-active": toCss({ ...primary, l: primary.l + 0.065 }),
    "on-primary": toCss(onPrimary),
    "primary-soft": toCss(soft),
    "primary-soft-hover": toCss(softHover),
    "primary-text": toCss(text),
    ring: toCss(ring),
    "ring-soft": toCss(ring, 0.34),
    scrim: toCss(BLACK, 0.6),
    "shadow-color": toCss(BLACK, 0.45),
    ...statusVars("dark", [surface, bg, raised]),
  };
}

const RADIUS_RE = /^(\d+(?:\.\d+)?)(px|rem)$/;
const MAX_RADIUS_PX = 24;
export const DEFAULT_RADIUS = "12px";

function parseRadius(value: string | undefined): string {
  const match = value ? RADIUS_RE.exec(value.trim()) : null;
  if (!match) return DEFAULT_RADIUS;
  const px = match[2] === "rem" ? Number(match[1]) * 16 : Number(match[1]);
  return `${Math.min(px, MAX_RADIUS_PX)}px`;
}

function safeLogo(value: string | undefined): string | null {
  if (!value) return null;
  try {
    const url = new URL(value);
    if (url.protocol === "https:" || url.protocol === "http:") return url.href;
    return /^data:image\/(png|jpeg|webp|gif);/.test(value) ? value : null;
  } catch {
    return null;
  }
}

export interface ResolvedTheme {
  mode: ThemeMode;
  light: ThemeVars;
  dark: ThemeVars;
  radius: string;
  logo: string | null;
  productName: string;
}

/**
 * Host white-label first, then the session token, so a host can theme one user differently. Only the known tokens are
 * read: an unknown name or a value that does not parse changes nothing, whatever the host sends.
 */
export function resolveTheme(theme: Theme | null | undefined, whiteLabel?: WhiteLabel | null): ResolvedTheme {
  const tokens = { ...whiteLabel?.theme?.tokens, ...theme?.tokens };
  const primary = (tokens.primary ? parseColor(tokens.primary) : null) ?? DEFAULT_PRIMARY;
  const onPrimary = tokens["on-primary"] ? parseColor(tokens["on-primary"]) : null;
  const name = (tokens["product-name"] ?? whiteLabel?.name)?.trim();
  return {
    mode: theme?.mode ?? whiteLabel?.theme?.mode ?? "system",
    light: lightVars(primary, onPrimary),
    dark: darkVars(primary, onPrimary),
    radius: parseRadius(tokens.radius),
    logo: safeLogo(tokens.logo ?? whiteLabel?.logoUrl),
    productName: name ? name.slice(0, 60) : DEFAULT_PRODUCT_NAME,
  };
}

/** Writes the palette for the mode. The only place in the app that sets a color. */
export function applyTheme(root: HTMLElement, theme: ResolvedTheme, mode: ResolvedMode): void {
  const vars = mode === "dark" ? theme.dark : theme.light;
  for (const [name, value] of Object.entries(vars)) root.style.setProperty(`--${name}`, value);
  root.style.setProperty("--radius", theme.radius);
  root.style.setProperty("color-scheme", mode);
  root.dataset.theme = mode;
}
